import React, { useState, useContext, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Slider from '@wix/da-shared-react/pkg/Slider';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import TiersSliderCard from './TiersSliderCard';
import AddTierCard from './AddTierCard';

import s from './Tiers.scss';

export interface Props {
  moduleId: number;
  tiers: PapiDeviation[];
  seeAllUrl?: string;
  isOwner: boolean;
  view?: 'promotion' | 'product';
  selectedTierId?: number;
  onClick?: (tier: PapiDeviation) => void;
  onSelect?: (tier: PapiDeviation) => void;
  withExpandButton?: boolean;
}

const Tiers: React.FC<Props> = ({
  tiers,
  isOwner,
  view = 'promotion',
  withExpandButton,
  selectedTierId,
  onClick,
  onSelect,
}) => {
  const findTierIndexByDeviationId = useCallback(
    id => tiers.findIndex(({ deviationId }) => deviationId === id),
    [tiers]
  );
  const isSubscribedToOne = tiers.some(
    deviation => deviation.tier?.isUserSubscribed
  );
  const selectedIndex = Math.max(0, findTierIndexByDeviationId(selectedTierId));
  const isMobile = useContext(MobileContext);
  const [isCollapsed, setIsCollapsed] = useState(isMobile || isSubscribedToOne);

  const { t } = useTranslation();
  const count = tiers.length;
  const showAddTierCard = isOwner && !isMobile;
  const showExpandAllButton = withExpandButton && !isMobile;

  return (
    <div className={s['root']}>
      <Slider
        className={classnames(s['slider'], s[view])}
        contentClassName={classnames(
          s['slider-content'],
          !isMobile && count < 4 && s['centered-slider-content']
        )}
        /* Owners have one extra slide at the beginning of the slider so offset index by one for owners */
        scrollToIndex={selectedIndex + (showAddTierCard ? 1 : 0)}
        scrollSnap={isMobile}
      >
        {showAddTierCard && (
          <AddTierCard
            count={count}
            className={s['add-tier-card']}
            view={view}
          />
        )}
        {tiers.map((tier, index) => (
          <TiersSliderCard
            key={tier.deviationId}
            tier={tier}
            onClick={onClick}
            onSelect={onSelect}
            view={view}
            withExpandButton={!showExpandAllButton}
            isCollapsed={isCollapsed}
            onToggleCollapsed={newIsCollapsed => setIsCollapsed(newIsCollapsed)}
            isSelected={view !== 'promotion' && selectedIndex === index}
          />
        ))}
      </Slider>

      {isMobile && tiers.length > 1 && (
        <div className={s['indicators']}>
          {tiers.map((_, index) => (
            <div
              key={index}
              className={classnames(
                s['indicator'],
                index === selectedIndex && s['active']
              )}
              onClick={() => onSelect && onSelect(tiers[index])}
            />
          ))}
        </div>
      )}

      {showExpandAllButton && (
        <div className={s['toggle-button-wrapper']}>
          <TextButton
            variant="main"
            size="large"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={s['toggle-button']}
            postfixIcon={isCollapsed ? ArrowDownIcon : ArrowUpIcon}
          >
            {isCollapsed
              ? t('widgets.tiers.showAllBenefits')
              : t('widgets.tiers.showLessBenefits')}
          </TextButton>
        </div>
      )}
    </div>
  );
};
Tiers.displayName = 'Tiers';

export default Tiers;
